import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';

interface CareHistoryItem {
  id: string;
  match_number: string;
  status: string;
  start_date: string;
  end_date: string;
  care_end_date: string;
  location: string;
  hourly_rate: number;
  daily_hours: number;
  total_amount: number;
  completed_at: string;
  caregiver_name: string;
  caregiver_phone: string;
}

const FEE_RATE = 0.05;

export default function AppPatientHistoryPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [history, setHistory] = useState<CareHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth.userId) return;
    loadHistory();
  }, [auth.userId]);

  async function loadHistory() {
    setLoading(true);
    try {
      const { data: matches } = await supabase
        .from('match_requests')
        .select('*')
        .or(`requester_id.eq.${auth.userId},receiver_id.eq.${auth.userId}`)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (!matches || matches.length === 0) {
        setHistory([]);
        setLoading(false);
        return;
      }

      const counterpartIds = matches.map((m) =>
        m.requester_id === auth.userId ? m.receiver_id : m.requester_id,
      );
      const uniqueIds = [...new Set(counterpartIds)];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, phone')
        .in('id', uniqueIds);
      const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

      const mapped: CareHistoryItem[] = matches.map((m) => {
        const counterpartId = m.requester_id === auth.userId ? m.receiver_id : m.requester_id;
        const cp = profileMap.get(counterpartId);
        return {
          id: m.id,
          match_number: m.match_number || '',
          status: m.status,
          start_date: m.start_date || '',
          end_date: m.end_date || '',
          care_end_date: m.care_end_date || m.completed_at?.split('T')[0] || '',
          location: m.location || '',
          hourly_rate: m.hourly_rate || 0,
          daily_hours: m.daily_hours || 0,
          total_amount: m.total_amount || 0,
          completed_at: m.completed_at || '',
          caregiver_name: cp?.name || '간병인',
          caregiver_phone: cp?.phone || '',
        };
      });
      setHistory(mapped);
    } catch (err) {
      console.error('Failed to load care history:', err);
    } finally {
      setLoading(false);
    }
  }

  const totalStats = (() => {
    const totalAmount = history.reduce((sum, h) => sum + (h.total_amount || 0), 0);
    const totalDays = history.reduce((sum, h) => {
      if (h.start_date && h.care_end_date) {
        const start = new Date(h.start_date);
        const end = new Date(h.care_end_date);
        const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        return sum + (days > 0 ? days : 0);
      }
      return sum;
    }, 0);
    return { totalAmount, totalDays, count: history.length };
  })();

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 상단 헤더 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-50"
          >
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <div>
            <h1 className="text-[22px] font-extrabold text-gray-900">간병 이력</h1>
            <p className="text-xs text-gray-400 mt-0.5">완료된 간병 기록을 확인하세요</p>
          </div>
        </div>

        {/* 전체 요약 통계 */}
        {history.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
            <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-bar-chart-2-line text-[#2E7D6B] text-lg" />
              전체 간병 실적 요약
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#f0f9f6] rounded-xl p-4 text-center">
                <p className="text-xl font-extrabold text-[#2E7D6B]">{totalStats.count}</p>
                <p className="text-[11px] text-[#2E7D6B]/60 font-medium mt-1">완료 건수</p>
              </div>
              <div className="bg-[#f0f9f6] rounded-xl p-4 text-center">
                <p className="text-xl font-extrabold text-[#2E7D6B]">{totalStats.totalDays}</p>
                <p className="text-[11px] text-[#2E7D6B]/60 font-medium mt-1">총 간병일</p>
              </div>
              <div className="bg-[#f0f9f6] rounded-xl p-4 text-center">
                <p className="text-xl font-extrabold text-[#2E7D6B]">
                  {totalStats.totalAmount.toLocaleString()}<span className="text-sm">원</span>
                </p>
                <p className="text-[11px] text-[#2E7D6B]/60 font-medium mt-1">총 간병비</p>
              </div>
            </div>
          </div>
        )}

        {/* 간병 이력 리스트 */}
        {history.length > 0 ? (
          <div>
            <h2 className="text-sm font-extrabold text-gray-900 mb-3">
              완료된 간병 내역 ({history.length}건)
            </h2>
            <div className="space-y-3">
              {history.map((item) => {
                const careDays = (() => {
                  if (item.start_date && item.care_end_date) {
                    const start = new Date(item.start_date);
                    const end = new Date(item.care_end_date);
                    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                    return days > 0 ? days : 0;
                  }
                  return 0;
                })();

                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl border border-gray-100 p-4"
                  >
                    {/* 상단: 상태 + 매칭번호 */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f0f9f6] text-[#2E7D6B] font-bold">
                        완료
                      </span>
                      <span className="text-[11px] text-gray-400">
                        매칭번호 {item.match_number}
                      </span>
                    </div>

                    {/* 간병인 정보 */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-[#f0f9f6] flex items-center justify-center">
                        <i className="ri-nurse-line text-sm text-[#2E7D6B]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{item.caregiver_name}</p>
                      </div>
                    </div>

                    {/* 간병 정보 그리드 */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-gray-400 mb-0.5">간병 기간</p>
                        <p className="text-xs font-bold text-gray-900">
                          {item.start_date} ~ {item.care_end_date}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{careDays}일</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-[10px] text-gray-400 mb-0.5">근무 조건</p>
                        <p className="text-xs font-bold text-gray-900">
                          {item.hourly_rate?.toLocaleString()}원 × {item.daily_hours || '-'}시간
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{item.location || '위치 미기재'}</p>
                      </div>
                    </div>

                    {/* 금액 정보 */}
                    <div className="border-t border-gray-50 pt-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">총 간병비</span>
                        <span className="font-extrabold text-[#2E7D6B]">{(item.total_amount || 0).toLocaleString()}원</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-400">
                        <i className="ri-information-line" />
                        <span>중개 수수료 {(FEE_RATE * 100).toFixed(0)}%는 간병인에게 부과됩니다</span>
                      </div>
                    </div>

                    {/* 완료일 */}
                    <div className="flex items-center gap-1 mt-3 text-[10px] text-gray-400">
                      <i className="ri-check-double-line" />
                      <span>
                        완료일: {new Date(item.completed_at).toLocaleDateString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#f0f9f6] flex items-center justify-center mx-auto mb-4">
              <i className="ri-history-line text-2xl text-[#2E7D6B]" />
            </div>
            <h3 className="text-base font-extrabold text-gray-900 mb-1">
              아직 완료된 간병이 없어요
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              매칭이 성사되고 간병이 완료되면 여기에 기록이 쌓입니다
            </p>
            <button
              onClick={() => navigate('/app/patient/search')}
              className="mt-4 px-5 py-2.5 rounded-xl bg-[#2E7D6B] text-white text-sm font-bold cursor-pointer hover:opacity-90"
            >
              간병인 찾으러 가기
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}